import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AgilityRiskTestComponent } from './components/test/agility-risk-test.component';
import { AgilityRiskComponent } from './agility-risk.component';
import { RiskDashboardComponent } from './components/risk-dashboard/risk-dashboard.component';
import { ThreatTypesComponent } from './components/risk-threat-types/threat-types.component';
import { RiskAssessmentSurveyComponent } from './components/risk-assessment/survey/risk-assessment-survey.component';
import { AgilityRiskRouteGuardService } from './providers/agility-risk-route-guard.service';
import { permissions } from './agility-risk.constants';
import { RiskAssessmentSummaryComponent } from './components/risk-assessment/summary/risk-assessment-summary.component';
import { RiskAssessmentComponent } from './components/risk-assessment/risk-assessment.component';
import { AgilityRiskSetupComponent } from './components/risk-setup/agility-risk-setup.component';
import { RiskDistributionComponent } from './components/risk-distribution/risk-distribution.component';
import { RiskFormulaManagementComponent } from './components/risk-formula-management/risk-formula-management.component';
import { RiskTemplatesComponent } from './components/risk-templates/risk-templates.component';
import { RiskAssessmentCompleteComponent } from './components/risk-assessment/complete/risk-assessment-complete.component';
import { AgilityRiskAssessmentRouteGuardService } from './providers/agility-risk-assessment-route-guard.service';
import { AssessmentFlow } from './agility-risk.enum';
import { ThreatQuestionsComponent } from './components/risk-templates/risk-template-management/threat-questions/threat-questions.component';
import { RiskTemplateManagementComponent } from './components/risk-templates/risk-template-management/risk-template-management/risk-template-management.component';
import { RiskAssessmentDetailsComponent } from './components/risk-assessment/details/risk-assessment-details.component';
import { CustomFieldsManagementComponent } from './components/custom-fields-management/custom-fields-management.component';
import { AccessManagerComponent } from './components/risk-templates/risk-template-access/access-manager/access-manager.component';
import { RiskTemplateAccessComponent } from './components/risk-templates/risk-template-access/risk-template-access.component';

const routes: Routes = [
  {
    // NOTE: use kebab case for routes
    path: '',
    component: AgilityRiskComponent,
    canDeactivate: [AgilityRiskRouteGuardService],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      {
        path: 'test',
        component: AgilityRiskTestComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.test'
        }
      },
      {
        path: 'dashboard',
        component: RiskDashboardComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.dashboard',
          mainModuleRoute: true
        }
      },
      {
        path: 'assessment/details/:assessmentId',
        component: RiskAssessmentDetailsComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.assessment-details'
        }
      },
      {
        path: 'threat-types',
        component: ThreatTypesComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.threat-types',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'setup',
        component: AgilityRiskSetupComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.setup',
          mainModuleRoute: true,
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'assessment',
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          anonymous: true
        },
        children: [
          {
            path: '',
            component: RiskAssessmentComponent,
            canActivate: [AgilityRiskAssessmentRouteGuardService],
            data: {
              key: 'risk.assessment',
              flowStep: AssessmentFlow.landing
            }
          },
          {
            path: 'survey/:id',
            component: RiskAssessmentSurveyComponent,
            canActivate: [AgilityRiskAssessmentRouteGuardService],
            data: {
              key: 'risk.assessment-survey',
              flowStep: AssessmentFlow.survey
            }
          },
          {
            path: 'summary/:id',
            component: RiskAssessmentSummaryComponent,
            canActivate: [AgilityRiskAssessmentRouteGuardService],
            data: {
              key: 'risk.assessment-summary',
              flowStep: AssessmentFlow.summary
            }
          },
          {
            path: 'complete',
            component: RiskAssessmentCompleteComponent,
            canActivate: [AgilityRiskAssessmentRouteGuardService],
            data: {
              key: 'risk.assessment-complete',
              flowStep: AssessmentFlow.complete
            }
          }
        ]
      },
      {
        path: 'distribution',
        component: RiskDistributionComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.distribution',
          mainModuleRoute: true,
          permissions: [
            permissions.hasManagerRoles
          ]
        }
      },
      {
        path: 'risk-templates',
        component: RiskTemplatesComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.templates',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'risk-template/:templateId/threat/:threatId/questions',
        component: ThreatQuestionsComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.threat-questions',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'formula-management',
        component: RiskFormulaManagementComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.formula-management',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'risk-template-management/:id',
        component: RiskTemplateManagementComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.template-management',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'risk-template-management',
        component: RiskTemplateManagementComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.template-management',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'risk-template-access',
        component: RiskTemplateAccessComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.template-access',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'custom-fields-management/:areaId',
        component: CustomFieldsManagementComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.custom-fields-management',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'risk-template-access-manager/:id',
        component: AccessManagerComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.template-access-manager',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      },
      {
        path: 'risk-template-access-manager',
        component: AccessManagerComponent,
        canActivate: [AgilityRiskRouteGuardService],
        data: {
          key: 'risk.template-access-manager',
          permissions: [
            permissions.hasAdminRoles
          ]
        }
      }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class AgilityRiskRoutingModule {
}
